import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CaretDown, X, ChartLineUp, SlidersHorizontal, Bank, UsersThree, Stack,
  Lightning, Gavel, Vault, HandCoins, Scroll, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { cn } from "@/lib/format";
import { GOV_NAV } from "@/lib/governance";

const ICONS = {
  ChartLineUp, SlidersHorizontal, Bank, UsersThree, Stack,
  Lightning, Gavel, Vault, HandCoins, Scroll, ClockCounterClockwise,
};

const isActive = (itemPath, govPath) => {
  if (!govPath) return false;
  if (itemPath === "/governance") return govPath === "/governance";
  return govPath === itemPath || govPath.startsWith(`${itemPath}/`);
};

const Item = ({ item, govPath, onGo }) => {
  const Icon = ICONS[item.icon] || ChartLineUp;
  const active = isActive(item.path, govPath);
  return (
    <button
      type="button"
      data-testid={`gov-nav-item-${item.path}`}
      onClick={() => onGo(item.path)}
      className={cn(
        "group flex items-start gap-3 w-full text-left rounded-lg px-3 py-2.5 transition-colors",
        active ? "bg-primary/10 border border-primary/30" : "border border-transparent hover:bg-muted/30",
      )}
    >
      <span className={cn("mt-0.5 shrink-0", active ? "text-primary" : "text-primary/60 group-hover:text-primary")}>
        <Icon size={18} weight={active ? "fill" : "regular"} />
      </span>
      <span className="min-w-0">
        <span className={cn("block text-[13px] font-medium", active ? "text-primary" : "text-foreground")}>{item.label}</span>
        <span className="block text-[11px] text-muted-foreground mono truncate">{item.desc}</span>
      </span>
    </button>
  );
};

export const GovNav = ({ navigate, govPath, inGovernance }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  // Only highlight an active item when actually on a governance page; otherwise
  // the default "/governance" would wrongly mark Overview active on explorer pages.
  const activePath = inGovernance ? govPath : null;

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  // Position the desktop dropdown relative to the trigger. The menu is rendered
  // through a portal to document.body so the header's backdrop-filter can't trap
  // its stacking context or clip the fixed mobile overlay.
  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    };
    update();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="gov-nav-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1.5 border px-3 py-1.5 rounded-lg transition-colors uppercase tracking-[0.2em] text-[11px] mono",
          inGovernance || open
            ? "border-primary/50 bg-primary/10 text-primary"
            : "border-primary/20 bg-card/40 text-muted-foreground hover:text-foreground hover:border-primary/40",
        )}
      >
        Governance
        <CaretDown size={11} weight="bold" className={cn("transition-transform", open && "rotate-180")} />
      </button>

      {open && createPortal(
        <>
          {/* click-away backdrop */}
          <button
            type="button"
            aria-label="Close governance menu"
            data-testid="gov-nav-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[100] bg-background/40 backdrop-blur-[1px] md:bg-transparent md:backdrop-blur-0 cursor-default"
          />

          {/* Desktop: mega dropdown, positioned under the trigger */}
          <div
            data-testid="gov-nav-dropdown"
            style={{ top: pos?.top ?? 64, right: pos?.right ?? 16 }}
            className="hidden md:grid fixed z-[101] grid-cols-5 gap-5 p-5 w-[min(1000px,92vw)] rounded-xl border border-primary/20 bg-card/95 backdrop-blur-md shadow-2xl shadow-black/40"
          >
            {GOV_NAV.map((col) => (
              <div key={col.heading} className="space-y-1.5">
                <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mono border-b border-primary/10 mb-1">
                  {col.heading}
                </div>
                {col.items.map((item) => (
                  <Item key={item.path} item={item} govPath={activePath} onGo={go} />
                ))}
              </div>
            ))}
          </div>

          {/* Mobile: full-screen overlay */}
          <div
            data-testid="gov-nav-mobile"
            className="md:hidden fixed inset-0 z-[101] bg-background flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-primary/20">
              <span className="font-heading text-[15px] font-bold tracking-wide text-foreground">Governance</span>
              <button
                type="button"
                data-testid="gov-nav-mobile-close"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-primary/20 text-muted-foreground hover:text-foreground"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
              {GOV_NAV.map((col) => (
                <div key={col.heading} className="space-y-1.5">
                  <div className="px-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mono">{col.heading}</div>
                  {col.items.map((item) => (
                    <Item key={item.path} item={item} govPath={activePath} onGo={go} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};
